import { Component, effect, inject, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CampaignFormPayloadService } from '@campaign-form/campaign-form-payload.service';
import { ICtaForm } from '@campaign-form/campaign-form.interface';
import { TCtaTemplateSort } from '@campaign-form/form-fields/creators-and-ctas/creators-and-ctas.interface';
import { CreatorsAndCtasService } from '@campaign-form/form-fields/creators-and-ctas/creators-and-ctas.service';
import { ICtaTemplateApi } from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.interface';
import { DeliverableFlowDialogComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-flow-dialog.component';
import {
  EDeliverableFlowStep,
  IDeliverableFlowDialogData,
  IDeliverableFlowDialogResult,
} from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-flow-dialog.interface';
import { TDeliverable } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { EExecutionType } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { GlobalDeliverablesListComponent } from '@campaign-form/form-fields/creators-and-ctas/global-deliverables-list/global-deliverables-list.component';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EEventType } from '@enums/event-tracking.enum';
import { TranslateService } from '@ngx-translate/core';
import { ALL_CREATORS_ID } from '@shared/constants/all-creators.constants';
import { toEventPlatformFeature } from '@utilities/event-payload-mapping.util';
import { v4 as uuidv4 } from 'uuid';
import {
  TGlobalTemplateApi,
  TGlobalTemplateCreatePayload,
  TGlobalTemplateUpdatePayload,
} from './template-settings.interface';
import { TemplateSettingsStore } from './template-settings.store';

@Component({
  imports: [GlobalDeliverablesListComponent],
  providers: [TemplateSettingsStore],
  selector: 'app-template-settings',
  templateUrl: './template-settings.component.html',
})
export class TemplateSettingsComponent {
  public readonly globalTemplates = signal<FormGroup<ICtaForm>['value'][]>([]);
  public readonly EDeliverableFlowStep = EDeliverableFlowStep;

  public readonly getExistingDeliverablesCountForGlobal = (ctaId: string, creatorId: string): number => {
    const cacheKey: `${string}_${string}` = `${ctaId}_${creatorId}`;

    return this.deliverablesCountCache.get(cacheKey) ?? 0;
  };
  public readonly getExistingExecutionsCountForGlobal = (ctaId: string, creatorId: string): number | null => {
    const cacheKey: `${string}_${string}` = `${ctaId}_${creatorId}`;

    return this.executionsCountCache.get(cacheKey) ?? null;
  };
  public readonly getExistingKpisCountForGlobal = (ctaId: string, creatorId: string): number => {
    const cacheKey: `${string}_${string}` = `${ctaId}_${creatorId}`;

    return this.kpisCountCache.get(cacheKey) ?? 0;
  };

  private readonly ctasValueMap = new Map<string, FormGroup<ICtaForm>['value']>();
  private readonly deliverablesCountCache = new Map<string, number>();
  private readonly executionsCountCache = new Map<string, number | null>();
  private readonly kpisCountCache = new Map<string, number>();

  public readonly store = inject(TemplateSettingsStore);
  private readonly campaignFormPayloadService = inject(CampaignFormPayloadService);
  private readonly creatorsAndCtasService = inject(CreatorsAndCtasService);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly eventTrackingService = inject(EventTrackingService);

  // LF-2 (audit 2026-08-06) - template ids whose edit-dialog close produced a save, consumed by
  // the updatedTemplate$ observer to fire campaign_creation_global_group_changed_saved. Revised
  // 2026-08-06: every non-discarded edit-mode close counts as a change (the template "changed"
  // signal is the edit-save itself), so this is set unconditionally in edit mode.
  private readonly pendingChangedTemplateIds = new Set<string>();

  constructor() {
    this.store.fetchGlobalTemplates();
    this.createTemplatesEffect();
    this.createTemplateSavedEventEffects();
  }

  public addGlobalDeliverables(): void {
    this.openDeliverableFlowDialog();
  }

  public addTemplateDeliverables(ctaId: string, startingStep: EDeliverableFlowStep): void {
    this.openDeliverableFlowDialog(ctaId, startingStep);
  }

  public removeTemplate(ctaId: string): void {
    // LF-2 - immediate global_group_removed, with the group-scoped counts (templates always belong
    // to the 'global' family - they're created with creatorIds [ALL_CREATORS_ID]).
    const ctaValues = this.ctasValueMap.get(ctaId);

    if (ctaValues?.id) {
      this.eventTrackingService.track(EEventType.campaignCreationGlobalGroupRemoved, {
        campaignCreationDeliverableGroupCounter: ctaValues.deliverables?.length,
        ctaTemplateId: ctaValues.id,
        platformFeature: ctaValues.platformFeature ? toEventPlatformFeature(ctaValues.platformFeature) : undefined,
      });
    }

    this.ctasValueMap.delete(ctaId);
    this.updateTemplatesFromMap();

    this.store.deleteGlobalTemplate(ctaId);
  }

  // LF-2 - the _saved counterparts to the immediate template group events. Templates' "save"
  // moment is the template CRUD itself (not a campaign section save), so these observe the store's
  // success signals: newTemplate$ (create-success, real id from the response), updatedTemplate$
  // (update-success, gated on the close handler having seen real field changes), deletedTemplate$
  // (delete-success).
  private createTemplateSavedEventEffects(): void {
    effect(() => {
      const createdTemplate = this.store.newTemplate$();

      if (!createdTemplate) {
        return;
      }

      this.eventTrackingService.track(EEventType.campaignCreationGlobalGroupAddedSaved, {
        campaignCreationDeliverableGroupCounter: CreatorsAndCtasService.getApiDeliverables(
          this.toTemplateWithCampaignId(createdTemplate),
        ).length,
        ctaTemplateId: createdTemplate.id,
        newValue: 'global',
        platformFeature: toEventPlatformFeature(createdTemplate.platformFeature),
      });
    });

    effect(() => {
      const updatedTemplate = this.store.updatedTemplate$();

      if (!updatedTemplate || !this.pendingChangedTemplateIds.has(updatedTemplate.id)) {
        return;
      }

      this.pendingChangedTemplateIds.delete(updatedTemplate.id);
      this.eventTrackingService.track(EEventType.campaignCreationGlobalGroupChangedSaved, {
        // Round-4 review (2026-08-06) - group-scoped deliverable count added for shape
        // consistency with the other template/group _saved events (platformFeature already there).
        campaignCreationDeliverableGroupCounter: CreatorsAndCtasService.getApiDeliverables(
          this.toTemplateWithCampaignId(updatedTemplate),
        ).length,
        ctaTemplateId: updatedTemplate.id,
        platformFeature: toEventPlatformFeature(updatedTemplate.platformFeature),
      });
    });

    effect(() => {
      const deletedTemplate = this.store.deletedTemplate$();

      if (!deletedTemplate) {
        return;
      }

      this.eventTrackingService.track(EEventType.campaignCreationGlobalGroupRemovedSaved, {
        campaignCreationDeliverableGroupCounter: CreatorsAndCtasService.getApiDeliverables(
          this.toTemplateWithCampaignId(deletedTemplate),
        ).length,
        ctaTemplateId: deletedTemplate.id,
        platformFeature: toEventPlatformFeature(deletedTemplate.platformFeature),
      });
    });
  }

  private createTemplatesEffect(): void {
    effect(() => {
      this.syncTemplates(this.store.templates$());
    });
  }

  private createDeliverableDialogCloseSubscription(dialogRef: MatDialogRef<DeliverableFlowDialogComponent>): void {
    dialogRef.afterClosed().subscribe((result: IDeliverableFlowDialogResult | undefined) => {
      const isDiscarded = Boolean(result?.discarded) as boolean;

      if (isDiscarded) {
        return;
      }

      if (!result?.ctaFormValues) {
        return;
      }

      const payload: TGlobalTemplateCreatePayload = this.mapCtaFormToPayload(result.ctaFormValues);
      this.addDefaultValues(payload);

      const ctaId: string | null = result.ctaFormValues.id ?? null;

      if (!ctaId) {
        return;
      }

      // LF-2 - the map still holds the pre-edit values here; that's the create-vs-edit signal and
      // the diff base for the immediate group events (the close result carries no before/after
      // diff of its own - same gap noted for the campaign-side group events).
      const previousValues = this.ctasValueMap.get(ctaId);
      const isExisting = !!previousValues;

      if (!isExisting) {
        this.trackTemplateGroupAdded(result.ctaFormValues);
      } else if (this.trackTemplateGroupChanged(previousValues, result.ctaFormValues)) {
        this.pendingChangedTemplateIds.add(ctaId);
      }

      this.ctasValueMap.set(ctaId, result.ctaFormValues);
      this.updateTemplatesFromMap();

      if (isExisting) {
        this.store.updateGlobalTemplate({
          id: ctaId,
          template: payload as TGlobalTemplateUpdatePayload,
        });

        return;
      }

      this.store.createGlobalTemplate({ tempId: ctaId, template: payload });
    });
  }

  // LF-2 - immediate global_group_added for a genuinely new template. No campaignId (template
  // settings has no campaign context - that absence is the intended discriminator), no
  // creatorId/creator counter (templates have no creators; always 'global' family).
  private trackTemplateGroupAdded(ctaFormValues: FormGroup<ICtaForm>['value']): void {
    this.eventTrackingService.track(EEventType.campaignCreationGlobalGroupAdded, {
      campaignCreationDeliverableGroupCounter: ctaFormValues.deliverables?.length,
      ctaTemplateId: ctaFormValues.id ?? undefined,
      newValue: 'global',
      platformFeature: ctaFormValues.platformFeature
        ? toEventPlatformFeature(ctaFormValues.platformFeature)
        : undefined,
    });
  }

  // LF-2 + ME-4 - immediate global_group_changed for an edited template. Fires on EVERY
  // non-discarded edit-mode close (revised 2026-08-06: the template "changed" signal is the
  // edit-save itself — deliverables/executions may have changed without a detectable
  // name/platform diff, so a diff-based gate missed those). Per-field source when detectable:
  // 'name' / 'platform_feature'; otherwise no source (deliverable-level edits). Always returns
  // true so the caller gates the _saved counterpart on the same unconditional basis.
  private trackTemplateGroupChanged(
    previousValues: FormGroup<ICtaForm>['value'],
    currentValues: FormGroup<ICtaForm>['value'],
  ): boolean {
    const ctaId = currentValues.id ?? undefined;
    const nameChanged = previousValues.name !== (currentValues.name ?? null);
    const platformFeatureChanged = (previousValues.platformFeature ?? null) !== (currentValues.platformFeature ?? null);
    // Round-4 review (2026-08-06) - every template group event now carries the group-scoped
    // deliverable count and platformFeature (matching the campaign-side group events' shape).
    const deliverableGroupCounter = currentValues.deliverables?.length;
    const platformFeature = currentValues.platformFeature
      ? toEventPlatformFeature(currentValues.platformFeature)
      : undefined;

    if (nameChanged) {
      this.eventTrackingService.track(EEventType.campaignCreationGlobalGroupChanged, {
        campaignCreationDeliverableGroupCounter: deliverableGroupCounter,
        ctaTemplateId: ctaId,
        newValue: currentValues.name ?? undefined,
        platformFeature,
        previousValue: previousValues.name ?? undefined,
        source: 'name',
      });
    }

    if (platformFeatureChanged) {
      this.eventTrackingService.track(EEventType.campaignCreationGlobalGroupChanged, {
        campaignCreationDeliverableGroupCounter: deliverableGroupCounter,
        ctaTemplateId: ctaId,
        newValue: currentValues.platformFeature ?? undefined,
        platformFeature,
        previousValue: previousValues.platformFeature ?? undefined,
        source: 'platform_feature',
      });
    }

    if (!nameChanged && !platformFeatureChanged) {
      this.eventTrackingService.track(EEventType.campaignCreationGlobalGroupChanged, {
        campaignCreationDeliverableGroupCounter: deliverableGroupCounter,
        ctaTemplateId: ctaId,
        // Round-3 audit - every group_* event carries platformFeature.
        platformFeature,
      });
    }

    return true;
  }

  // Mirrors the templateWithCampaignId construction in syncTemplates() - TGlobalTemplateApi's
  // campaignId is optional/nullable, getApiDeliverables() wants ICtaTemplateApi.
  private toTemplateWithCampaignId(template: TGlobalTemplateApi): ICtaTemplateApi {
    return {
      ...template,
      campaignId: template.campaignId ?? '',
    };
  }

  private mapCtaFormToPayload(cta: FormGroup<ICtaForm>['value']): TGlobalTemplateCreatePayload {
    const templates = this.campaignFormPayloadService.getMappedCtaTemplates(null, [], [cta]);

    return templates[0] ?? ({} as TGlobalTemplateCreatePayload);
  }

  private openDeliverableFlowDialog(ctaId?: string | null, startingStep?: EDeliverableFlowStep): void {
    const data: IDeliverableFlowDialogData = {
      campaignCreatorsValue: [],
      creatorIds: [ALL_CREATORS_ID],
      ctasValues: Array.from(this.ctasValueMap.values()),
      isTemplateSettings: true,
    };

    if (ctaId) {
      const ctaValues: FormGroup<ICtaForm>['value'] | undefined = this.ctasValueMap.get(ctaId);

      if (!ctaValues) {
        return;
      }

      data.ctaValues = ctaValues;
      data.createMode = false;
      data.startingStep = startingStep ?? EDeliverableFlowStep.deliverable;
    }

    const dialogRef: MatDialogRef<DeliverableFlowDialogComponent> = this.dialog.open(DeliverableFlowDialogComponent, {
      data,
      disableClose: true,
      maxHeight: '90vh',
      width: '700px',
    });

    this.createDeliverableDialogCloseSubscription(dialogRef);
  }

  private syncTemplates(templates: TGlobalTemplateApi[]): void {
    this.ctasValueMap.clear();

    templates.forEach((template) => {
      const templateWithCampaignId: ICtaTemplateApi = {
        ...template,
        campaignId: template.campaignId ?? '',
      };
      const deliverables: TDeliverable[] = CreatorsAndCtasService.getApiDeliverables(templateWithCampaignId);
      const executions = this.creatorsAndCtasService.getExecutions(templateWithCampaignId) ?? [];
      const ctaForm: FormGroup<ICtaForm> = this.creatorsAndCtasService.createCtaFormFromValues(
        templateWithCampaignId,
        deliverables,
        executions,
        false,
      );

      this.ctasValueMap.set(template.id, ctaForm.value);
    });

    const sortedTemplates: FormGroup<ICtaForm>['value'][] = this.creatorsAndCtasService.sortCtaTemplates(
      Array.from(this.ctasValueMap.values()) as TCtaTemplateSort[],
    );

    this.globalTemplates.set(sortedTemplates);
    this.updateCaches();
  }

  private updateTemplatesFromMap(): void {
    const sortedTemplates: FormGroup<ICtaForm>['value'][] = this.creatorsAndCtasService.sortCtaTemplates(
      Array.from(this.ctasValueMap.values()) as TCtaTemplateSort[],
    );

    this.globalTemplates.set(sortedTemplates);
    this.updateCaches();
  }

  private updateCaches(): void {
    this.creatorsAndCtasService.updateCtaInformationCache(
      [],
      this.deliverablesCountCache,
      this.executionsCountCache,
      this.kpisCountCache,
      this.globalTemplates(),
    );
  }

  private addDefaultValues(payload: TGlobalTemplateCreatePayload): void {
    this.addDefaultName(payload);
    this.addDefaultExecution(payload);
  }

  private addDefaultExecution(payload: TGlobalTemplateCreatePayload): void {
    if (payload.executions?.length) {
      return;
    }

    payload.executions = [
      {
        customCtaValues: [],
        duration: null,
        exclusiveTo: null,
        executionCountTarget: 1,
        executionDate: null,
        executionTime: null,
        executionType: EExecutionType.undesignated,
        expectedKpis: [],
        globalCtaValues: [],
        id: uuidv4(),
        name: 'Default Execution',
      },
    ];
  }

  private addDefaultName(payload: TGlobalTemplateCreatePayload): void {
    if (payload.name) {
      return;
    }

    payload.name = this.translate.instant(`shared.platformFeatures.${payload.platformFeature}`);
  }
}
